import {
  HalfFloatType,
  Mesh,
  NoColorSpace,
  OrthographicCamera,
  PlaneGeometry,
  RenderTarget,
  Scene,
  Vector4,
  type Renderer,
  type Texture,
} from 'three/webgpu';
import { texture as textureNode } from 'three/tsl';

import { FFT_SIZE, readNormalisedWaveform } from '../../audio/analyser';
import { vss30 } from '../../audio/synth';
import { COLORS } from '../../core/constants';
import { TRANSPORT_STATES } from '../../audio/Scheduler';
import { EDIT_MODES, RECORD_STATES, type PanelState } from '../../store/panelTypes';
import { COUNT_IN } from '../../store/panelStore';
import { SCREEN_RECTS, SCREEN_SIZE, inset, type Rect } from './layout';
import {
  bloomable,
  createEnvelopePanel,
  createProgressPanel,
  createReverbPanel,
  createTrailMaterial,
  createWaveformPanel,
} from './panels';
import { FILTER_CURVE, RAMPS, createCurveTexture, createWaveformTexture } from './waveform';

/**
 * Fraction of the display that survives one second.
 *
 * The 2D version washed the canvas with 60%-opaque black on each of its 12
 * updates per second, so this is the same falloff expressed per second and is
 * now independent of the frame rate.
 */
const TRAIL_PER_SECOND = 0.4 ** 12;

/** Rate the drawn envelope chases the stored one, per frame at 60fps. */
const ADSR_SMOOTHING = 0.35;

const UNIT_PLANE = /*#__PURE__*/ new PlaneGeometry(1, 1);

/** The transport and sampling indicators, each driven far enough to bloom. */
const RING_COLORS = {
  red: bloomable(COLORS.red),
  blue: bloomable(COLORS.blue),
  yellow: bloomable(COLORS.yellow),
};

const EDIT_STAGE_INDEX: Partial<Record<PanelState['EDIT_MODE'], number>> = {
  [EDIT_MODES.ADSR_A]: 0,
  [EDIT_MODES.ADSR_D]: 1,
  [EDIT_MODES.ADSR_S]: 2,
  [EDIT_MODES.ADSR_R]: 3,
};

/**
 * The instrument's display, rendered on the GPU.
 *
 * The 2D canvas this replaces redrew every stroke on the CPU at 12fps and
 * uploaded a 512² texture each time. Here the same artwork is a handful of
 * quads carrying TSL signed-distance materials, drawn into a render target that
 * ping-pongs with itself so the waveforms keep their phosphor trails.
 *
 * The atlas layout is unchanged, so the UV mapping baked into the instrument's
 * `Display_*` meshes still lines up.
 */
export class ScreenTexture {
  readonly scene = new Scene();
  /**
   * Canvas-space camera: world coordinates are atlas pixels with the origin at
   * the top-left and +y running *down*, so a panel's rect can be used as-is.
   *
   * `top` and `bottom` are deliberately swapped, which puts atlas row 0 at v=0
   * in the render target. That inverts triangle winding, so the screen
   * materials are all double-sided.
   */
  readonly camera = new OrthographicCamera(0, SCREEN_SIZE, 0, SCREEN_SIZE, -1, 1);

  /** Sample this in any material that shows the display. */
  readonly node = textureNode(null as unknown as Texture);

  private targets: [RenderTarget, RenderTarget];
  private writeIndex = 0;

  private waveformTexture = createWaveformTexture(FFT_SIZE);
  private waveformData = new Float32Array(FFT_SIZE);

  private envelope = createEnvelopePanel({
    rect: inset(SCREEN_RECTS.ADSR),
    outerRect: SCREEN_RECTS.ADSR,
  });
  private loop = createWaveformPanel(this.waveformTexture, {
    rect: inset(SCREEN_RECTS.LOOP),
    sampleCount: FFT_SIZE,
    skip: 16,
    ramp: RAMPS.loop,
  });
  private pingPong = createWaveformPanel(this.waveformTexture, {
    rect: inset(SCREEN_RECTS.PING_PONG),
    sampleCount: FFT_SIZE,
    skip: 16,
    ramp: RAMPS.pingpong,
  });
  private reverse = createWaveformPanel(this.waveformTexture, {
    rect: inset(SCREEN_RECTS.REVERSE),
    sampleCount: FFT_SIZE,
    skip: 64,
    ramp: RAMPS.reverse,
  });
  private echo = createWaveformPanel(this.waveformTexture, {
    rect: inset(SCREEN_RECTS.ECHO),
    sampleCount: FFT_SIZE,
    skip: 32,
    ramp: RAMPS.echo,
  });
  private fuzz = createWaveformPanel(this.waveformTexture, {
    rect: inset(SCREEN_RECTS.FUZZ),
    sampleCount: FFT_SIZE,
    skip: 32,
    ramp: RAMPS.fuzz,
  });
  private filter = createWaveformPanel(createCurveTexture(FILTER_CURVE), {
    rect: inset(SCREEN_RECTS.FILTER),
    sampleCount: FILTER_CURVE.length,
    skip: 1,
    centred: false,
    amplitude: 1,
  });
  private reverb = createReverbPanel(inset(SCREEN_RECTS.REVERB));

  private sampling = createProgressPanel(inset(SCREEN_RECTS.RECORD_PROGRESS));
  private musicLoop = createProgressPanel(inset(SCREEN_RECTS.MUSIC_PROGRESS));
  private demo = createProgressPanel(inset(SCREEN_RECTS.DEMO_PROGRESS));

  /** Smoothed copy of the panel ADSR, so the envelope eases between values. */
  private drawnAdsr = new Vector4(0.0001, 1, 0.5, 1);

  private trail: ReturnType<typeof createTrailMaterial>;

  constructor() {
    this.targets = [this.createTarget(), this.createTarget()];
    this.node.value = this.targets[0].texture;

    // Redraws the previous frame, dimmed, before anything else this frame.
    this.trail = createTrailMaterial(this.node);
    const trailQuad = new Mesh(UNIT_PLANE, this.trail.material);
    trailQuad.scale.set(SCREEN_SIZE, SCREEN_SIZE, 1);
    trailQuad.position.set(SCREEN_SIZE / 2, SCREEN_SIZE / 2, -0.5);
    trailQuad.renderOrder = -1;
    trailQuad.frustumCulled = false;
    this.scene.add(trailQuad);

    this.place(inset(SCREEN_RECTS.ADSR), this.envelope.material);
    this.place(inset(SCREEN_RECTS.LOOP), this.loop.material);
    this.place(inset(SCREEN_RECTS.PING_PONG), this.pingPong.material);
    this.place(inset(SCREEN_RECTS.REVERSE), this.reverse.material);
    this.place(inset(SCREEN_RECTS.ECHO), this.echo.material);
    this.place(inset(SCREEN_RECTS.FUZZ), this.fuzz.material);
    this.place(inset(SCREEN_RECTS.FILTER), this.filter.material);
    this.place(inset(SCREEN_RECTS.REVERB), this.reverb.material);
    this.place(inset(SCREEN_RECTS.RECORD_PROGRESS), this.sampling.material);
    this.place(inset(SCREEN_RECTS.MUSIC_PROGRESS), this.musicLoop.material);
    this.place(inset(SCREEN_RECTS.DEMO_PROGRESS), this.demo.material);
  }

  /** Pulls fresh audio and panel state into the uniforms. Call once per frame. */
  update(state: PanelState, delta: number): void {
    readNormalisedWaveform(this.waveformData);
    (this.waveformTexture.image.data as Float32Array).set(this.waveformData);
    this.waveformTexture.needsUpdate = true;

    // Frame-rate independent ease toward the stored envelope.
    const ease = 1 - (1 - ADSR_SMOOTHING) ** (delta * 60);
    const [a, d, s, r] = state.ADSR;
    this.drawnAdsr.x += (a - this.drawnAdsr.x) * ease;
    this.drawnAdsr.y += (d - this.drawnAdsr.y) * ease;
    this.drawnAdsr.z += (s - this.drawnAdsr.z) * ease;
    this.drawnAdsr.w += (r - this.drawnAdsr.w) * ease;
    this.envelope.uniforms.adsr.value.copy(this.drawnAdsr);
    this.envelope.uniforms.editStage.value = EDIT_STAGE_INDEX[state.EDIT_MODE] ?? -1;

    this.loop.uniforms.active.value = state.LOOP ? 1 : 0;
    this.pingPong.uniforms.active.value = state.PINGPONG ? 1 : 0;
    this.reverse.uniforms.active.value = state.REVERSE ? 1 : 0;
    this.echo.uniforms.active.value = state.ECHO ? 1 : 0;
    this.fuzz.uniforms.active.value = state.FUZZ ? 1 : 0;
    this.filter.uniforms.active.value = state.FILTER ? 1 : 0;
    this.reverb.uniforms.active.value = state.REVERB ? 1 : 0;

    this.trail.uniforms.decay.value = TRAIL_PER_SECOND ** delta;

    this.updateSampling(state);
    this.updateTransport(state);
  }

  /** Draws the display into the next target and swaps. */
  render(renderer: Renderer): void {
    const read = this.targets[this.writeIndex];
    const write = this.targets[this.writeIndex ^ 1];

    // The trail quad samples this node, so point it at the newest frame before
    // drawing into the other target.
    this.node.value = read.texture;

    const previousTarget = renderer.getRenderTarget();
    renderer.setRenderTarget(write);
    renderer.render(this.scene, this.camera);
    renderer.setRenderTarget(previousTarget);

    this.writeIndex ^= 1;
    this.node.value = write.texture;
  }

  dispose(): void {
    for (const target of this.targets) target.dispose();
    this.waveformTexture.dispose();
    this.scene.traverse((object) => {
      if (object instanceof Mesh) object.material.dispose();
    });
  }

  private updateSampling(state: PanelState): void {
    const { uniforms } = this.sampling;
    const pulse = Math.abs(Math.sin(performance.now() * 0.005));

    switch (state.RECORD_STATE) {
      case RECORD_STATES.COUNTING_IN: {
        const elapsed = performance.now() - state.RECORD_START;
        uniforms.progress.value = 1 - Math.min(1, elapsed / COUNT_IN);
        uniforms.ringOpacity.value = 1;
        uniforms.discOpacity.value = 0;
        uniforms.ringColor.value.copy(RING_COLORS.blue);
        break;
      }
      case RECORD_STATES.RECORDING:
      case RECORD_STATES.OVERWRITING:
        uniforms.progress.value = vss30.samplingProgress;
        uniforms.ringOpacity.value = 1;
        uniforms.discOpacity.value = pulse;
        uniforms.ringColor.value.copy(RING_COLORS.red);
        uniforms.discColor.value.copy(RING_COLORS.red);
        break;
      default:
        uniforms.ringOpacity.value = 0;
        uniforms.discOpacity.value = 0;
        break;
    }
  }

  private updateTransport(state: PanelState): void {
    const pulse = Math.abs(Math.sin(performance.now() * 0.005));

    const demo = this.demo.uniforms;
    const playing = state.DEMO_STATE === TRANSPORT_STATES.PLAYING;
    demo.progress.value = state.MUSIC_RECORDER_POS;
    demo.ringOpacity.value = playing ? 1 : 0;
    demo.discOpacity.value = 0;
    demo.ringColor.value.copy(RING_COLORS.yellow);

    const music = this.musicLoop.uniforms;
    music.progress.value = state.MUSIC_RECORDER_POS;
    switch (state.MUSIC_RECORDER_STATE) {
      case TRANSPORT_STATES.WAITING_FOR_INPUT:
        music.ringOpacity.value = 0;
        music.discOpacity.value = pulse;
        music.discColor.value.copy(RING_COLORS.blue);
        break;
      case TRANSPORT_STATES.RECORDING:
        music.ringOpacity.value = 0;
        music.discOpacity.value = pulse;
        music.discColor.value.copy(RING_COLORS.red);
        break;
      case TRANSPORT_STATES.PLAYING:
        music.ringOpacity.value = 1;
        music.discOpacity.value = 0;
        music.ringColor.value.copy(RING_COLORS.blue);
        break;
      default:
        music.ringOpacity.value = 0;
        music.discOpacity.value = 0;
        break;
    }
  }

  private createTarget(): RenderTarget {
    // Float, so the indicators can be driven past white and bloom.
    const target = new RenderTarget(SCREEN_SIZE, SCREEN_SIZE, {
      type: HalfFloatType,
      depthBuffer: false,
      stencilBuffer: false,
      generateMipmaps: true,
    });
    target.texture.name = 'VSS30.Display';
    target.texture.colorSpace = NoColorSpace;
    return target;
  }

  private place(rect: Rect, material: Mesh['material']): void {
    const mesh = new Mesh(UNIT_PLANE, material);
    mesh.scale.set(rect.width, rect.height, 1);
    mesh.position.set(rect.x + rect.width / 2, rect.y + rect.height / 2, 0);
    mesh.frustumCulled = false;
    this.scene.add(mesh);
  }
}
